import type { Flux, LienFlux, NoeudFlux } from '@admin-pmvb/core'

// Mise en page d'un diagramme de flux en colonnes (type Sankey) : chaque nœud dans sa colonne,
// hauteur selon ce qui le traverse, bandes d'épaisseur proportionnelle à la racine du volume (les
// mails écartés, nombreux, n'écrasent pas une pièce isolée), ordre des nœuds d'une colonne choisi
// d'après ceux qui l'alimentent pour limiter les croisements. Pur : testé sans navigateur.

export const BARRE = 10
/** Hauteur d'une ligne de libellé ; un libellé tient sur deux lignes au plus. */
export const HAUTEUR_LIGNE = 14
const MARGE_LIBELLE = 6
const ESPACE = 8
const ENTETE = 28
const ENTETE_GROUPE = 20
const BARRE_MIN = 3

export type NoeudPlace = NoeudFlux & {
  x: number
  /** Haut de l'emplacement (libellé compris) et de la barre. */
  y: number
  yBarre: number
  hBarre: number
  hEmplacement: number
  /** Libellé coupé en lignes (deux au plus), le chiffre à la fin de la dernière. */
  lignes: string[]
  chiffre: number
  entrant: LienFlux[]
  sortant: LienFlux[]
}

export type LienPlace = LienFlux & {
  id: string
  epaisseur: number
  x0: number
  y0: number
  x1: number
  y1: number
}

export type Disposition = {
  noeuds: NoeudPlace[]
  liens: LienPlace[]
  largeur: number
  hauteur: number
  colonnesX: number[]
  /** Largeur disponible pour le libellé d'un nœud de chaque colonne. */
  largeursLibelle: number[]
  groupes: { libelle: string; x: number; y: number }[]
}

const valeur = (liens: readonly LienFlux[]) => liens.reduce((s, l) => s + l.valeur, 0)

const somme = (liens: readonly LienFlux[]) => liens.reduce((s, l) => s + Math.sqrt(l.valeur), 0)

export function disposerFlux(
  flux: Flux,
  options: { largeur: number; hauteurMax: number; largeurLibelleFin?: number },
): Disposition {
  const nbColonnes = flux.colonnes.length
  const largeurLibelleFin = options.largeurLibelleFin ?? 180
  const pas = nbColonnes > 1 ? (options.largeur - BARRE - largeurLibelleFin) / (nbColonnes - 1) : 0
  const colonnesX = flux.colonnes.map((_, c) => c * pas)
  const largeursLibelle = flux.colonnes.map((_, c) =>
    c === nbColonnes - 1 ? largeurLibelleFin - 8 : pas - BARRE - 10,
  )

  const entrant = new Map<string, LienFlux[]>()
  const sortant = new Map<string, LienFlux[]>()
  for (const n of flux.noeuds) {
    entrant.set(n.id, [])
    sortant.set(n.id, [])
  }
  const liens = flux.liens.filter((l) => entrant.has(l.vers) && sortant.has(l.de))
  for (const l of liens) {
    entrant.get(l.vers)!.push(l)
    sortant.get(l.de)!.push(l)
  }
  const brut = new Map(
    flux.noeuds.map((n) => [n.id, Math.max(somme(entrant.get(n.id)!), somme(sortant.get(n.id)!))]),
  )
  const chiffres = new Map(
    flux.noeuds.map((n) => [n.id, valeur(sortant.get(n.id)!) || valeur(entrant.get(n.id)!)]),
  )
  const lignes = new Map(
    flux.noeuds.map((n) => [
      n.id,
      couperLibelle(n.libelle, largeursLibelle[n.colonne]! - 18, String(chiffres.get(n.id))),
    ]),
  )
  const minimum = (id: string) => lignes.get(id)!.length * HAUTEUR_LIGNE + MARGE_LIBELLE
  const emplacement = (k: number, id: string) =>
    Math.max(Math.max(k * brut.get(id)!, BARRE_MIN), minimum(id))
  const colonnes = flux.colonnes.map((_, c) => flux.noeuds.filter((n) => n.colonne === c))
  const groupesColonne0 = new Set(colonnes[0]?.map((n) => n.groupe ?? '')).size

  // Échelle : la plus haute colonne tient dans la hauteur voulue.
  const hauteurColonne = (k: number, c: number) =>
    colonnes[c]!.reduce(
      (s, n) => s + emplacement(k, n.id) + ESPACE,
      c === 0 ? groupesColonne0 * ENTETE_GROUPE : 0,
    )
  const tropHaut = (k: number) =>
    colonnes.some((_, c) => hauteurColonne(k, c) > options.hauteurMax - ENTETE)
  let bas = 0
  let haut = 12
  for (let i = 0; i < 40; i++) {
    const k = (bas + haut) / 2
    if (tropHaut(k)) haut = k
    else bas = k
  }
  const k = bas
  const hauteurUtile = Math.max(
    ...colonnes.map((_, c) => hauteurColonne(k, c)),
    HAUTEUR_LIGNE + MARGE_LIBELLE + ESPACE,
  )

  const places = new Map<string, NoeudPlace>()
  const groupes: Disposition['groupes'] = []
  const placer = (n: NoeudFlux, y: number) => {
    const hBarre = Math.max(k * brut.get(n.id)!, BARRE_MIN)
    const hEmplacement = emplacement(k, n.id)
    const p: NoeudPlace = {
      ...n,
      x: colonnesX[n.colonne]!,
      y,
      yBarre: y + (hEmplacement - hBarre) / 2,
      hBarre,
      hEmplacement,
      lignes: lignes.get(n.id)!,
      chiffre: chiffres.get(n.id)!,
      entrant: entrant.get(n.id)!,
      sortant: sortant.get(n.id)!,
    }
    places.set(n.id, p)
    return p
  }
  const centre = (id: string) => {
    const p = places.get(id)!
    return p.yBarre + p.hBarre / 2
  }

  // Première colonne : dans l'ordre donné, par source.
  let y = ENTETE
  let groupe: string | undefined
  for (const n of colonnes[0] ?? []) {
    if ((n.groupe ?? '') !== groupe) {
      groupe = n.groupe ?? ''
      if (groupe) groupes.push({ libelle: groupe, x: 0, y: y + 12 })
      y += ENTETE_GROUPE
    }
    y += placer(n, y).hEmplacement + ESPACE
  }

  // Colonnes suivantes : chaque nœud au plus près du centre de ce qui l'alimente, sans
  // chevauchement, dans la hauteur commune.
  const bas0 = ENTETE + hauteurUtile
  for (let c = 1; c < nbColonnes; c++) {
    const voulus = colonnes[c]!.map((n, rang) => {
      const e = entrant.get(n.id)!.filter((l) => places.has(l.de))
      const poids = somme(e)
      const bary = poids
        ? e.reduce((s, l) => s + centre(l.de) * Math.sqrt(l.valeur), 0) / poids
        : Number.POSITIVE_INFINITY
      const hEmplacement = emplacement(k, n.id)
      return { n, rang, bary, hEmplacement }
    })
    voulus.sort((a, b) => a.bary - b.bary || a.rang - b.rang)
    const tops: number[] = []
    let suivant = ENTETE
    for (const v of voulus) {
      const desire = Number.isFinite(v.bary) ? v.bary - v.hEmplacement / 2 : suivant
      const top = Math.max(desire, suivant)
      tops.push(top)
      suivant = top + v.hEmplacement + ESPACE
    }
    // Ce qui déborde en bas remonte, sans passer au-dessus de l'en-tête.
    let limite = bas0
    for (let i = voulus.length - 1; i >= 0; i--) {
      tops[i] = Math.max(ENTETE, Math.min(tops[i]!, limite - voulus[i]!.hEmplacement))
      limite = tops[i]! - ESPACE
    }
    voulus.forEach((v, i) => placer(v.n, tops[i]!))
  }

  // Ports : les bandes sortent et entrent dans l'ordre vertical de l'autre bout.
  const epaisseur = (l: LienFlux) => k * Math.sqrt(l.valeur)
  const departs = new Map<LienFlux, number>()
  const arrivees = new Map<LienFlux, number>()
  for (const p of places.values()) {
    const sorties = [...p.sortant].sort((a, b) => centre(a.vers) - centre(b.vers))
    let o = p.yBarre + (p.hBarre - sorties.reduce((s, l) => s + epaisseur(l), 0)) / 2
    for (const l of sorties) {
      departs.set(l, o + epaisseur(l) / 2)
      o += epaisseur(l)
    }
    const entrees = [...p.entrant].sort((a, b) => centre(a.de) - centre(b.de))
    o = p.yBarre + (p.hBarre - entrees.reduce((s, l) => s + epaisseur(l), 0)) / 2
    for (const l of entrees) {
      arrivees.set(l, o + epaisseur(l) / 2)
      o += epaisseur(l)
    }
  }

  const hauteur = Math.max(...[...places.values()].map((p) => p.y + p.hEmplacement), ENTETE) + 8
  return {
    noeuds: [...places.values()],
    liens: liens.map((l) => ({
      ...l,
      id: `${l.de}>${l.vers}>${l.unite}`,
      epaisseur: Math.max(epaisseur(l), 1.5),
      x0: places.get(l.de)!.x + BARRE,
      y0: departs.get(l)!,
      x1: places.get(l.vers)!.x,
      y1: arrivees.get(l)!,
    })),
    largeur: options.largeur,
    hauteur,
    colonnesX,
    largeursLibelle,
    groupes,
  }
}

/** Chemin d'une bande : courbe en S d'un bout à l'autre. */
export function cheminLien(l: Pick<LienPlace, 'x0' | 'y0' | 'x1' | 'y1'>): string {
  const m = (l.x0 + l.x1) / 2
  return `M${l.x0},${l.y0} C${m},${l.y0} ${m},${l.y1} ${l.x1},${l.y1}`
}

/** Tout ce qui mène à un nœud et tout ce qui en découle : le chemin à mettre en avant. */
export function cheminDe(
  depart: { noeud: string } | { lien: Pick<LienFlux, 'de' | 'vers'> },
  liens: readonly Pick<LienPlace, 'id' | 'de' | 'vers'>[],
): { noeuds: Set<string>; liens: Set<string> } {
  const noeuds = new Set<string>()
  const vus = new Set<string>()
  const amont: string[] = []
  const aval: string[] = []
  if ('noeud' in depart) {
    amont.push(depart.noeud)
    aval.push(depart.noeud)
  } else {
    amont.push(depart.lien.de)
    aval.push(depart.lien.vers)
    const id = liens.find((l) => l.de === depart.lien.de && l.vers === depart.lien.vers)?.id
    if (id) vus.add(id)
  }
  for (const [pile, sens] of [
    [amont, 'amont'],
    [aval, 'aval'],
  ] as const) {
    const faits = new Set<string>()
    while (pile.length) {
      const id = pile.pop()!
      if (faits.has(id)) continue
      faits.add(id)
      noeuds.add(id)
      for (const l of liens)
        if (sens === 'amont' ? l.vers === id : l.de === id) {
          vus.add(l.id)
          pile.push(sens === 'amont' ? l.de : l.vers)
        }
    }
  }
  return { noeuds, liens: vus }
}

/** Coupe un libellé à la largeur disponible (estimation pour une police de 12 px). */
export function tronquer(texte: string, largeurPx: number, taille = 12): string {
  const max = Math.max(4, Math.floor(largeurPx / (taille * 0.54)))
  return texte.length <= max ? texte : `${texte.slice(0, max - 1).trimEnd()}…`
}

/**
 * Libellé en deux lignes au plus, coupé aux espaces, en gardant la place du chiffre au bout de la
 * dernière ; au-delà, la seconde ligne finit par « … ».
 */
export function couperLibelle(texte: string, largeurPx: number, chiffre: string): string[] {
  const max = Math.max(6, Math.floor(largeurPx / (12 * 0.54)))
  const place = chiffre.length + 1
  if (texte.length + place <= max) return [texte]
  // Une adresse se coupe avant l'arobase.
  const mots = texte.includes(' ') ? texte.split(' ') : texte.replace('@', ' @').split(' ')
  let premiere = ''
  const colle = !texte.includes(' ')
  const joindre = (a: string, b: string) => (colle ? `${a}${b}` : `${a} ${b}`.trim())
  while (mots.length && joindre(premiere, mots[0]!).length <= max)
    premiere = joindre(premiere, mots.shift()!)
  if (!premiere) return [tronquer(texte, (max - place) * 12 * 0.54)]
  const reste = colle ? mots.join('') : mots.join(' ')
  return [premiere, tronquer(reste, (max - place) * 12 * 0.54)]
}
